/**
 * CosmeticAvatar — the composited avatar preview for the Wave 2 shop.
 *
 * v1 ships PLACEHOLDER geometric/recolour layers (roadmap decision 2026-06-24:
 * "ship first with simple geometric/recolour layers as a stand-in so the
 * economy + flow can be built and tested before final art lands"). Real
 * hand-crafted SVG cosmetic layers are a later design drop — they will replace
 * the renderers here but reuse the same `asset_key` → layer mapping, so nothing
 * downstream changes.
 *
 * Composition model (one equipped item per kind — enforced server-side by
 * cosmetics.service.equipItem):
 *   - theme     → a full-bleed tinted backdrop behind the portrait
 *   - frame     → a coloured ring around the portrait
 *   - accessory → a small bar "hat" across the top of the portrait
 *   - pet       → a small circle "companion" in the bottom-right
 *
 * Colours come from the brand @theme tokens (var(--color-*)) chosen
 * deterministically from the asset_key hash — so a given cosmetic always
 * renders the same placeholder colour, and there are zero inline hex literals
 * (DASH-10 lint stays green).
 */
import type { ReactNode } from 'react';
import type { CosmeticKind } from '@stell/shared';
import { PixelAvatar, type ColorSlot } from '../../components/primitives/PixelAvatar';

export interface EquippedLayer {
  kind: CosmeticKind;
  asset_key: string;
}

/** Lagene for det medlemmet har på seg, fra eide kosmetikk-rader (Butikk og Meg). */
export function equippedLayers(
  owned: readonly (EquippedLayer & { equipped: boolean })[],
): EquippedLayer[] {
  return owned.filter((o) => o.equipped).map((o) => ({ kind: o.kind, asset_key: o.asset_key }));
}

// Brand accent tokens used to colour the placeholder layers. Token names only —
// no hex (DASH-10). Rolletokens; rekkefølgen er den samme som da listen var
// pigmentnavn (terracotta, ochre, sage, sky, plum, berry), så hver kosmetikk
// beholder fargen sin (#161). Navnet bygges dynamisk, så theme.test sitt søk
// etter fjernede tokens ser det ikke — hold listen til rolletokens.
const LAYER_TOKENS = [
  'primary',
  'warn',
  'good',
  'blue',
  'lilac',
  'bad',
] as const;

/** Deterministic token pick from an asset key (stable per cosmetic). */
function tokenForKey(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 31 + key.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % LAYER_TOKENS.length;
  return `var(--color-${LAYER_TOKENS[idx]})`;
}

interface CosmeticAvatarProps {
  colorSlot: ColorSlot;
  equipped: EquippedLayer[];
  size?: number;
  className?: string;
}

export function CosmeticAvatar({
  colorSlot,
  equipped,
  size = 96,
  className,
}: CosmeticAvatarProps): ReactNode {
  const byKind = new Map<CosmeticKind, EquippedLayer>();
  for (const layer of equipped) byKind.set(layer.kind, layer);

  const theme = byKind.get('theme');
  const frame = byKind.get('frame');
  const accessory = byKind.get('accessory');
  const pet = byKind.get('pet');

  const cls = ['relative inline-block', className].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      style={{ width: size, height: size }}
      data-testid="cosmetic-avatar"
    >
      {/* theme — tinted backdrop */}
      {theme ? (
        <div
          aria-hidden
          data-testid="cosmetic-layer-theme"
          className="absolute inset-0 rounded-2xl"
          style={{
            background: `color-mix(in oklab, ${tokenForKey(theme.asset_key)} 30%, var(--color-raised))`,
          }}
        />
      ) : null}

      {/* frame — coloured ring */}
      {frame ? (
        <div
          aria-hidden
          data-testid="cosmetic-layer-frame"
          className="absolute inset-0 rounded-2xl"
          style={{
            border: `${Math.max(2, Math.round(size * 0.04))}px solid ${tokenForKey(frame.asset_key)}`,
          }}
        />
      ) : null}

      {/* base portrait, inset slightly so the frame reads as a border */}
      <div className="absolute inset-[8%] flex items-center justify-center">
        <PixelAvatar colorSlot={colorSlot} size={Math.round(size * 0.84)} />
      </div>

      {/* accessory — hat bar across the top */}
      {accessory ? (
        <div
          aria-hidden
          data-testid="cosmetic-layer-accessory"
          className="absolute left-1/2 -translate-x-1/2 rounded-full"
          style={{
            top: `${Math.round(size * 0.06)}px`,
            width: `${Math.round(size * 0.46)}px`,
            height: `${Math.round(size * 0.1)}px`,
            background: tokenForKey(accessory.asset_key),
          }}
        />
      ) : null}

      {/* pet — companion circle bottom-right */}
      {pet ? (
        <div
          aria-hidden
          data-testid="cosmetic-layer-pet"
          className="absolute rounded-full border-2 border-raised"
          style={{
            right: 0,
            bottom: 0,
            width: `${Math.round(size * 0.26)}px`,
            height: `${Math.round(size * 0.26)}px`,
            background: tokenForKey(pet.asset_key),
          }}
        />
      ) : null}
    </div>
  );
}
